// Libraries
import React from 'react';

// Supermove
import {Form, Styled} from '@supermove/components';
import {gql} from '@supermove/graphql';
import {useResponsive} from '@supermove/hooks';
import {LocationModel, OrganizationModel} from '@supermove/models';

// App
import JobAdditionalFields from 'modules/Job/components/JobAdditionalFields';
import JobBillingFields from 'modules/Job/components/JobBillingFields';
import JobInternalFields from 'modules/Job/components/JobInternalFields';
import JobLocationsFields from 'modules/Job/components/JobLocationsFields';
import JobScheduleFields from 'modules/Job/components/JobScheduleFields';

const Container = Styled.View`
  padding-horizontal: 30px;
  z-index: 100;
`;

const Space = Styled.View<{mobile: boolean}>`
  margin-top: ${({mobile}) => (mobile ? 30 : 40)}px;
`;

const Section = Styled.View<{index: number}>`
  z-index: ${({index}) => 100 - index};
`;

type Props = {
  field: string;
  organization: OrganizationModel;
  form: Form;
  jobIndex: number;
  isUpdate?: boolean;
  shouldResetJobDateOnJobTypeChange: boolean;
  jobFormField: string;
  warehouseLocation: LocationModel | null;
  mapRef: React.RefObject<any>;
  jobMapComponent: React.ReactNode;
  handleShowSalesCalendar?: () => void;
};

const JobFields = ({
  field,
  organization,
  form,
  jobIndex,
  isUpdate = false,
  shouldResetJobDateOnJobTypeChange,
  jobFormField,
  warehouseLocation,
  mapRef,
  jobMapComponent,
  handleShowSalesCalendar,
}: Props) => {
  const responsive = useResponsive();
  const jobField = `${field}.jobForms.${jobIndex}`;
  return (
    <React.Fragment>
      <Container>
        <Section index={0}>
          <JobScheduleFields
            index={0}
            field={jobField}
            form={form}
            responsive={responsive}
            organization={organization}
            shouldResetJobDateOnJobTypeChange={shouldResetJobDateOnJobTypeChange}
            jobFormField={jobFormField}
            handleShowSalesCalendar={handleShowSalesCalendar}
          />
          <Space {...responsive} />
          <JobLocationsFields
            index={1}
            field={jobField}
            form={form}
            responsive={responsive}
            organization={organization}
            warehouseLocation={warehouseLocation}
            mapRef={mapRef}
            jobMapComponent={jobMapComponent}
          />
          <Space {...responsive} />
          <JobBillingFields
            isUpdate={isUpdate}
            index={2}
            field={field}
            jobField={jobField}
            form={form}
            responsive={responsive}
            organization={organization}
          />
          <Space {...responsive} />
        </Section>
        <JobAdditionalFields
          index={1}
          field={jobField}
          form={form}
          responsive={responsive}
          organization={organization}
        />
      </Container>
      <JobInternalFields
        index={2}
        field={field}
        jobField={jobField}
        form={form}
        organization={organization}
        responsive={responsive}
      />
    </React.Fragment>
  );
};

// --------------------------------------------------
// Data
// --------------------------------------------------
JobFields.fragment = gql`
  ${JobAdditionalFields.fragment}
  ${JobBillingFields.fragment}
  ${JobInternalFields.fragment}
  ${JobLocationsFields.fragment}
  ${JobScheduleFields.fragment}

  fragment JobFields on Organization {
    id
    ...JobAdditionalFields
    ...JobBillingFields
    ...JobInternalFields
    ...JobLocationsFields
    ...JobScheduleFields
  }
`;

export default JobFields;
